import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@dia/i18n';
import { loadShell, type ContentDoc } from '@dia/content';
import { LangToggle, Screen } from '@dia/ui';
import { useAuth } from '../auth';
import { BackLink } from './parts/BackLink';
import { dateOnly } from './logic/accessWindow';
import { GuideCard } from './parts/GuideCard';
import { PhotoPanel } from './parts/PhotoPanel';
import { Prose } from './parts/Prose';
import { TopicGrid, type Topic } from './parts/TopicGrid';
import {
  BedIcon,
  BoatIcon,
  CheckoutIcon,
  GiftIcon,
  HotTubIcon,
  HouseIcon,
  InfoIcon,
  MoonIcon,
  PalmIcon,
  RulesIcon,
  SearchIcon,
  SunIcon,
  WavesIcon,
  WifiIcon,
} from './parts/icons';
import { bodyFor } from './logic/bilingual';
import { firstParagraph } from './logic/guideSections';
import { searchGuide, type SearchableDoc } from './logic/guideSearch';

const content = loadShell('guest');

/**
 * The House Guide, Touch Stay style (docs/GUEST-REDESIGN.md Section 5): a
 * cover, the three questions every guest asks first pinned at the top, a
 * search box, then a photo grid of topics. This is the destination for the
 * House Guide tile the home screen links to, at the same `/casas` address the
 * QR codes and the old screen already used, so nothing that points here needs
 * to change.
 *
 * Public and bundled: it opens with no signal and no sign in, the same as
 * every guide page. Where a card needs the guest's own stay (their tier, their
 * departure date), it says so plainly and points at signing in rather than
 * guessing.
 */
export function HouseGuides() {
  const { t, lang } = useT();
  const { session } = useAuth();
  const [query, setQuery] = useState('');

  const stayDocs = content.section('stay');
  const wifiDoc = stayDocs.find((d) => d.slug === 'wifi');
  const checkoutDoc = stayDocs.find((d) => d.slug === 'checkout');
  const rulesDoc = stayDocs.find((d) => d.slug === 'house-rules');
  const sleepDoc = stayDocs.find((d) => d.slug === 'where-you-sleep');
  const crewDoc = stayDocs.find((d) => d.slug === 'crew-and-quiet');
  const tier = session?.stay.tier ?? null;
  const includedDoc = tier ? stayDocs.find((d) => d.tiers.includes(tier)) : undefined;

  const houseDocs = content.section('houses');
  const bySlug = (slug: string) => houseDocs.find((d) => d.slug === slug);

  const topics: Topic[] = useMemo(() => {
    const forHouse = (
      key: string,
      slug: string,
      icon: Topic['icon'],
      photoTodo: string,
    ): Topic | undefined => {
      const houseDoc = bySlug(slug);
      return houseDoc ? { key, to: `/casas/${slug}`, label: houseDoc.title[lang], icon, photoTodo } : undefined;
    };

    const items: Array<Topic | undefined> = [
      {
        key: 'incluido',
        to: '/casas/incluido',
        label: includedDoc ? includedDoc.title[lang] : t('guest.guide.topic_included'),
        icon: <GiftIcon />,
        photoTodo: 'the kitchen and the main living room',
      },
      forHouse('hacienda', 'hacienda', <HouseIcon />, 'the hacienda from outside'),
      forHouse('bungalow', 'bungalow', <HouseIcon />, 'the bungalow'),
      forHouse('pabellon', 'pabellon', <SunIcon />, 'the solar pavilion'),
      forHouse('piscina', 'piscina', <WavesIcon />, 'the pool'),
      forHouse('jacuzzi', 'jacuzzi', <HotTubIcon />, 'the hot tub'),
      forHouse('muelles', 'muelles', <BoatIcon />, 'the two main docks'),
      forHouse('how-it-works', 'how-it-works', <InfoIcon />, 'the shower controls and the air conditioning remote'),
      crewDoc
        ? {
            key: 'crew-and-quiet',
            to: '/casas/crew-and-quiet',
            label: crewDoc.title[lang],
            icon: <MoonIcon />,
            photoTodo: 'the crew at work, no faces',
          }
        : undefined,
      {
        key: 'island',
        to: '/isla',
        label: t('guest.island.title'),
        icon: <PalmIcon />,
        photoTodo: 'snorkel spots, a sunrise and a sunset, the mangrove edge',
      },
    ];
    return items.filter((i): i is Topic => Boolean(i));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, tier]);

  const searchable: SearchableDoc[] = useMemo(() => {
    const docs: SearchableDoc[] = houseDocs.map((doc) => ({
      key: doc.path,
      title: doc.title[lang],
      lede: doc.lede?.[lang],
      body: bodyFor(doc.body, lang),
      target: { kind: 'route', to: `/casas/${doc.slug}` },
    }));
    if (crewDoc) {
      docs.push({
        key: crewDoc.path,
        title: crewDoc.title[lang],
        lede: crewDoc.lede?.[lang],
        body: bodyFor(crewDoc.body, lang),
        target: { kind: 'route', to: '/casas/crew-and-quiet' },
      });
    }
    if (includedDoc) {
      docs.push({
        key: includedDoc.path,
        title: includedDoc.title[lang],
        lede: includedDoc.lede?.[lang],
        body: bodyFor(includedDoc.body, lang),
        target: { kind: 'route', to: '/casas/incluido' },
      });
    }
    const pinned: Array<[ContentDoc | undefined, string]> = [
      [wifiDoc, 'wifi-card'],
      [checkoutDoc, 'checkout-card'],
      [rulesDoc, 'rules-card'],
      [sleepDoc, 'sleep-card'],
    ];
    for (const [doc, id] of pinned) {
      if (!doc) continue;
      docs.push({
        key: doc.path,
        title: doc.title[lang],
        lede: doc.lede?.[lang],
        body: bodyFor(doc.body, lang),
        target: { kind: 'anchor', id },
      });
    }
    return docs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, tier]);

  const hits = query.trim() ? searchGuide(searchable, query) : [];

  return (
    <Screen actions={<LangToggle />}>
      <BackLink />

      <section className="relative -mx-4 mt-2 h-64 overflow-hidden rounded-b-2xl">
        <PhotoPanel
          src="/photos/island-from-water.webp"
          alt=""
          todo="the hacienda, or the island from the water"
          captionCorner="top-right"
        />
        <div className="absolute inset-x-0 bottom-0 bg-ground/85 px-4 pb-4 pt-6">
          <h1 className="font-display text-[1.7em] leading-tight text-ink">{t('guest.guide.cover_title')}</h1>
          <p className="mt-1 text-inkMuted">{t('guest.guide.cover_sub')}</p>
        </div>
      </section>

      <div className="mt-6 flex flex-col gap-4">
        {wifiDoc && (
          <GuideCard id="wifi-card" icon={<WifiIcon />} title={t('guest.stay.wifi')}>
            <div className="flex items-center justify-between gap-3 rounded-xl bg-surfaceDeep px-4 py-3">
              <b className="text-[1.1em] text-ink">{(wifiDoc.meta.network as string) ?? 'Dia Bonito Guest'}</b>
              <CopyButton value={(wifiDoc.meta.network as string) ?? 'Dia Bonito Guest'} />
            </div>
            <Prose doc={wifiDoc} />
          </GuideCard>
        )}

        {checkoutDoc && (
          <GuideCard id="checkout-card" icon={<CheckoutIcon />} title={t('guest.guide.checkout_title')}>
            {session ? (
              <>
                <p className="text-[1.05em] font-medium text-ink">
                  {new Intl.DateTimeFormat(lang, { dateStyle: 'long', timeZone: 'UTC' }).format(
                    new Date(`${dateOnly(session.stay.departs_on)}T12:00:00Z`),
                  )}
                </p>
                <Prose doc={checkoutDoc} />
              </>
            ) : (
              <p className="text-inkMuted">
                {t('guest.guide.checkout_sign_in')}{' '}
                <Link to="/entrar" className="text-accent underline underline-offset-4">
                  {t('guest.arrive.sign_in')}
                </Link>
              </p>
            )}
          </GuideCard>
        )}

        {rulesDoc && (
          <GuideCard id="rules-card" icon={<RulesIcon />} title={t('guest.guide.rules_title')}>
            <Prose doc={rulesDoc} />
          </GuideCard>
        )}

        {sleepDoc && (
          <GuideCard id="sleep-card" icon={<BedIcon />} title={sleepDoc.title[lang]}>
            <p className="text-inkMuted">{firstParagraph(bodyFor(sleepDoc.body, lang))}</p>
            <Link
              to="/cuartos"
              className="inline-flex min-h-tap items-center rounded bg-accent px-4 text-onAccent font-medium w-fit"
            >
              {t('guest.nav.rooms')}
            </Link>
          </GuideCard>
        )}

        <div>
          <label className="flex items-center gap-2 rounded-xl border border-line bg-surface px-4 min-h-tap text-inkFaint">
            <SearchIcon className="h-5 w-5 shrink-0" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('guest.guide.search_placeholder')}
              aria-label={t('guest.guide.search_placeholder')}
              className="min-h-tap w-full bg-transparent py-2 text-ink placeholder:text-inkFaint focus:outline-none"
            />
          </label>

          {query.trim() && (
            <div className="mt-3 flex flex-col gap-2" role="region" aria-live="polite">
              <p className="text-caption text-inkFaint">
                {hits.length > 0
                  ? t('guest.guide.search_results', { count: hits.length, query })
                  : t('guest.guide.search_none')}
              </p>
              {hits.map((hit) =>
                hit.target.kind === 'route' ? (
                  <Link
                    key={hit.key}
                    to={hit.target.to}
                    className="rounded-xl border border-line bg-surface p-3 min-h-tap"
                  >
                    <span className="block font-medium text-ink">{hit.title}</span>
                    {hit.snippet && <span className="block text-caption text-inkFaint">{hit.snippet}</span>}
                  </Link>
                ) : (
                  <a
                    key={hit.key}
                    href={`#${hit.target.id}`}
                    className="rounded-xl border border-line bg-surface p-3 min-h-tap"
                  >
                    <span className="block font-medium text-ink">{hit.title}</span>
                    {hit.snippet && <span className="block text-caption text-inkFaint">{hit.snippet}</span>}
                  </a>
                ),
              )}
            </div>
          )}
        </div>

        <section aria-labelledby="browse">
          <h2 id="browse" className="font-display text-[1.25em] text-ink mb-3">
            {t('guest.guide.browse_title')}
          </h2>
          <TopicGrid topics={topics} />
        </section>
      </div>
    </Screen>
  );
}

function CopyButton({ value }: { value: string }) {
  const { t } = useT();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused. The name is right there to read and copy by hand.
    }
  };

  return (
    <button
      type="button"
      onClick={() => void copy()}
      className="min-h-tap rounded-full border border-accent px-4 text-caption font-medium text-accent"
    >
      {copied ? t('guest.guide.wifi_copied') : t('guest.guide.wifi_copy')}
    </button>
  );
}
